// Detects platform + PWA install state, and captures the Android
// `beforeinstallprompt` event so we can trigger installation on demand.
//
// iOS Safari does not fire `beforeinstallprompt` and cannot be installed
// programmatically — the user must tap Share → "Sur l'écran d'accueil".
// We detect iOS to show a dedicated instructions screen.

let deferredPrompt = null;
const listeners = new Set();

function notify() {
  listeners.forEach((cb) => {
    try { cb(); } catch (e) { console.debug("[install] listener error:", e?.message || e); }
  });
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify();
  });
}

export function subscribeInstall(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  const iosStandalone = typeof navigator !== "undefined" && navigator.standalone === true;
  const mqStandalone = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  return iosStandalone || mqStandalone;
}

export function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(ua) || iPadOS;
}

export function isIOSSafari() {
  if (!isIOS()) return false;
  const ua = navigator.userAgent || "";
  // Exclude Chrome/Firefox/Edge/Facebook/Instagram in-app browsers on iOS
  // (they all use WebKit but cannot install the PWA).
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|FBAN|FBAV|Instagram/.test(ua);
}

export function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent || "");
}

export function canPromptInstall() {
  return deferredPrompt !== null;
}

/**
 * Fire the native Android install prompt.
 * Returns 'accepted' | 'dismissed' | 'unavailable'.
 */
export async function promptInstall() {
  if (!deferredPrompt) return "unavailable";
  try {
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    deferredPrompt = null;
    notify();
    return outcome; // 'accepted' | 'dismissed'
  } catch (e) {
    console.debug("[install] promptInstall failed:", e?.message || e);
    return "unavailable";
  }
}
